/** @jsxImportSource @emotion/react */
import {
  getCurrencyName,
  Icon,
  IconName,
  createStyles,
  List,
  ListItem,
  useTheme,
  Modal,
  CryptoCurrency,
} from '@quickbit/qb-design-system';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { Wallet } from 'types';

interface Props {
  wallets: Wallet[];
  value: string | undefined;
  onChange: (wallet: Wallet) => void;
  firstSelect?: boolean;
}

const getCurrencyBlockchain = (currency: CryptoCurrency) => {
  switch (currency) {
    case 'BTC':
      return 'Bitcoin';
    case 'BCH':
      return 'Bitcoin Cash';
    case 'LTC':
      return 'Litecoin';
    case 'ETH':
      return 'Ethereum';
    case 'USDC':
      return 'Ethereum';
  }
  return '';
};

export const WalletSelect = ({
  wallets,
  value,
  onChange,
  firstSelect,
}: Props) => {
  const { formatMessage } = useIntl();
  const [showWalletList, setShowWalletList] = useState(firstSelect);
  const [tentativeWalletSelection, setTentativeWalletSelection] =
    useState<Wallet>();
  const { spacing } = useTheme();
  const styles = useStyles();

  const onWalletSelected = (selectedWallet: Wallet) => {
    onChange(selectedWallet);
    setShowWalletList(false);
  };

  if (!wallets.length) return null;

  const wallet = wallets.find((w) => w.currency === value);

  return (
    <>
      <li css={styles.default}>
        <ul
          css={[styles.selectWallet, wallet ? undefined : {cursor: 'pointer'}]}
          onClick={wallet ? undefined : () => setShowWalletList(!showWalletList)}
        >
          {!wallet && (
            <ListItem
              title={formatMessage({ id: 'walletSelect.placeholder' })}
              action={
                <div
                  css={[
                    styles.chevronContainer,
                    showWalletList && { transform: 'rotate(90deg)' },
                  ]}
                >
                  <Icon name="chevron" size={spacing.xxs} />
                </div>
              }
            />
          )}
          {wallet && (
            <ListItem
              icon={wallet.currency as IconName}
              title={getCurrencyName(wallet.currency)}
              subtitle={formatMessage(
                { id: 'page.home.walletSelect.blockchain' },
                {
                  blockchain: getCurrencyBlockchain(
                    wallet.currency as CryptoCurrency
                  ),
                }
              )}
            />
          )}
        </ul>
        {showWalletList && (
          <div css={styles.walletList}>
            <List>
              {wallets.map((wallet) => (
                <ListItem
                  key={wallet.currency}
                  icon={wallet.currency as IconName}
                  title={getCurrencyName(wallet.currency)}
                  subtitle={formatMessage(
                    { id: 'page.home.walletSelect.blockchain' },
                    {
                      blockchain: getCurrencyBlockchain(
                        wallet.currency as CryptoCurrency
                      ),
                    }
                  )}
                  onClick={() =>
                    firstSelect
                      ? onWalletSelected(wallet)
                      : setTentativeWalletSelection(wallet)
                  }
                />
              ))}
            </List>
          </div>
        )}
      </li>
      {tentativeWalletSelection && (
        <Modal
          title={formatMessage({ id: 'page.home.walletSelect.modal.title' })}
          subtitle={formatMessage({
            id: 'page.home.walletSelect.modal.subtitle',
          })}
          closeModal={() => setTentativeWalletSelection(undefined)}
          primaryButtonProps={{
            text: formatMessage(
              { id: 'page.home.walletSelect.modal.changeCurrency' },
              { currency: getCurrencyName(tentativeWalletSelection.currency) }
            ),
            icon: tentativeWalletSelection.currency as IconName,
            onClick: () => onWalletSelected(tentativeWalletSelection),
          }}
          secondaryButtonProps={{
            text: formatMessage({ id: 'page.home.walletSelect.modal.cancel' }),
            onClick: () => setTentativeWalletSelection(undefined),
          }}
        />
      )}
    </>
  );
};

const useStyles = createStyles(({ palette, spacing, shape }) => ({
  default: {
    position: 'relative',
    width: '100%',
    borderBottom: `1px solid ${palette.grey.x100}`,
  },
  chevronContainer: {
    marginTop: spacing.xxxs,
    transform: 'rotate(-90deg)',
    svg: { fill: 'none', stroke: palette.black },
  },
  selectWallet: {
    width: '100%',
    borderRadius: shape.borderRadius,
    listStyleType: 'none',
    margin: 0,
    padding: 0,
  },
  walletList: {
    width: '100%',
    height: 'auto',
    maxHeight: 300,
    marginTop: spacing.xxxs,
    overflow: 'hidden',
    overflowY: 'scroll',
    position: 'absolute',
    top: '100%',
    left: 0,
    border: `1px solid ${palette.grey.x100}`,
    borderRadius: shape.borderRadius,
    boxShadow: `0 4px 4px 0 rgba(0 0 0 / 0.1)`,
    zIndex: '10',

    '& > ul': {
      border: 'none',
    },
  },
}));
